import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { UserProfile, FinancialConfig, Material } from '../types';
import { 
  ArrowLeft, 
  User as UserIcon, 
  Building2, 
  MapPin, 
  ShieldCheck, 
  ChevronRight, 
  CheckCircle2,
  Mail,
  Phone,
  Calendar,
  CreditCard,
  Briefcase,
  ImageIcon,
  Receipt,
  DollarSign,
  Percent,
  LogOut,
  Cloud,
  CloudOff,
  CloudUpload,
  Loader2,
  PlayCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { NICHES, TUTORIAL_LINKS } from '../constants';
import { categorizeNiche } from '../utils/nicheUtils';
import { calculatePriceHealth } from '../utils/gamificationUtils';
import NicheResetModal from './NicheResetModal';
import { AlertTriangle, Timer, Info, X } from 'lucide-react';
import TermsContent from './legal/TermsContent';
import PrivacyContent from './legal/PrivacyContent';
import PaywallModal from './PaywallModal';
import { HideOnPlayStore } from './HideOnPlayStore';
import { formatCurrencyBRL, formatCurrencyMask, parseCurrencyMask } from '../utils/formatUtils'; // Added formatCurrencyMask, parseCurrencyMask
import { compressImage } from '../utils/imageUtils';
import { extractDominantColor } from '../utils/colorExtractor';
import CancelSurveyModal from './profile/CancelSurveyModal';
import LgpdStep from './profile/LgpdStep';
import PersonalStep from './profile/PersonalStep';
import LocationStep from './profile/LocationStep';
import FinancialStep from './profile/FinancialStep';
import BusinessStep from './profile/BusinessStep';
import { useProfessions } from '../hooks/useProfessions';


interface ProfileEditorProps {
  user: UserProfile;
  config: FinancialConfig;
  materials: Material[];
  onSave: (user: UserProfile, config: FinancialConfig) => Promise<void>;
  onResetNicheData?: () => Promise<void>;
  onClose: () => void;
  onLogout?: () => void;
  initialStep?: Step;
}

type Step = 'personal' | 'business' | 'location' | 'lgpd' | 'financial'; // Added 'financial' step

export default function ProfileEditor({ user, config, materials, onSave, onResetNicheData, onClose, onLogout, initialStep }: ProfileEditorProps) {
  const [step, setStep] = useState<Step>(initialStep || 'business');
  const [formData, setFormData] = useState<UserProfile>(() => {
    if (!user.lgpdConsent) {
      return {
        ...user,
        lgpdConsent: {
          termsAccepted: true,
          privacyPolicyAccepted: true,
          dataProcessingAccepted: true,
          marketingAccepted: false,
          timestamp: new Date().toISOString()
        }
      };
    }
    return user;
  });
  const [financialData, setFinancialData] = useState<FinancialConfig>(config);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [pendingNiche, setPendingNiche] = useState<{name: string, category: string, professionName?: string} | null>(null);
  const [isNicheOpen, setIsNicheOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { isLoading: isSearchingProfessions, searchProfessions } = useProfessions();
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSaved, setLastSaved] = useState<string | null>(null);

  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const [isPaywallOpen, setIsPaywallOpen] = useState(false);
  const [paywallInitialPlan, setPaywallInitialPlan] = useState<'monthly' | 'annual' | 'specialist' | 'specialist_annual'>('annual');
  
  // Churn/Cancel states
  const [isCancelSurveyOpen, setIsCancelSurveyOpen] = useState(false);
  const [cancelStep, setCancelStep] = useState<'survey' | 'retention_price' | 'retention_roadmap' | 'confirm'>('survey');
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [isCanceling, setIsCanceling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelSuccess, setCancelSuccess] = useState(false);

  const handleCancelSubscription = async () => {
    setIsCanceling(true);
    setCancelError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("Usuário não autenticado.");
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ reason: selectedReason })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Erro ao processar o cancelamento da assinatura.");
      }

      const resData = await response.json();
      
      if (resData.success) {
        setCancelSuccess(true);
        const updatedUser: UserProfile = {
          ...formData,
          plan: 'Grátis',
          subscriptionStatus: 'canceled',
          cancelReason: selectedReason
        };
        setFormData(updatedUser);
        onSave(updatedUser, financialData);
      } else {
        throw new Error("Não foi possível cancelar.");
      }
    } catch (err: unknown) {
      console.error(err);
      const errMsg = err instanceof Error ? err.message : "Erro de conexão ao tentar cancelar a assinatura. Tente novamente.";
      setCancelError(errMsg);
    } finally {
      setIsCanceling(false);
    }
  };

  // Auto-save logic
  const triggerAutoSave = React.useCallback(async (updatedUser?: UserProfile, updatedFinancial?: FinancialConfig) => {
    const finalUser = updatedUser || formData;
    const finalFinancial = updatedFinancial || financialData;
    
    if (import.meta.env.DEV) {
      console.log('R-MAX Auto-Save Triggered:', {
        user: finalUser,
        financial: finalFinancial,
        calculatedVHB: finalFinancial.baseHourValue
      });
    }
    
    setIsSyncing(true);
    try {
      await onSave(finalUser, finalFinancial);
      setLastSaved(new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }));
    } catch (err) {
      console.error('Auto-save error:', err);
    } finally {
      setIsSyncing(false);
    }
  }, [formData, financialData, onSave]);

  // Debounced save for text fields
  const debouncedFormData = React.useRef<UserProfile | null>(null);
  const syncTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const professionSyncTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    return () => {
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
      if (professionSyncTimerRef.current) clearTimeout(professionSyncTimerRef.current);
    };
  }, []);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (debouncedFormData.current) {
        triggerAutoSave(debouncedFormData.current, financialData);
        debouncedFormData.current = null;
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, [formData, financialData, triggerAutoSave]);

  const numberInputRef = React.useRef<HTMLInputElement>(null);
  const zipCodeChangedByUser = React.useRef(false);

  const normalizeText = (text: string) => 
    text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

  const validateField = (name: string, value: unknown) => {
    let error = '';
    const valStr = typeof value === 'string' ? value : '';
    switch (name) {
      case 'ownerName':
        if (!valStr || valStr.trim().length < 3) error = 'Nome muito curto';
        break;
      case 'birthDate':
        if (!value) error = 'Data obrigatória';
        break;
      case 'email':
        if (!valStr || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(valStr)) error = 'E-mail inválido';
        break;
      case 'phone':
        if (!valStr || valStr.replace(/\D/g, '').length < 10) error = 'Telefone inválido';
        break;
      case 'businessName':
        if (!valStr || valStr.trim().length < 2) error = 'Nome da empresa obrigatório';
        break;
      case 'taxRegime':
        if (!value) error = 'Selecione um regime';
        break;
      case 'niche':
        if (!value) error = 'Selecione um nicho';
        break;
      case 'zipCode':
        if (!valStr || !/^\d{5}-?\d{3}$/.test(valStr)) error = 'CEP inválido';
        break;
      case 'businessAddress':
        if (!valStr || valStr.trim().length < 5) error = 'Endereço obrigatório';
        break;
      case 'city':
        if (!value) error = 'Cidade obrigatória';
        break;
      case 'state':
        if (!valStr || valStr.length !== 2) error = 'UF inválida (2 letras)';
        break;
    }
    setErrors(prev => ({ ...prev, [name]: error }));
    return !error;
  };

  const isStepValid = (currentStep: Step) => {
    return true; // Fast navigation mode
  };

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      await onSave(formData, financialData);
      onClose(); // Só fecha se a promessa resolver com sucesso
    } catch (err: unknown) {
      console.error('Erro ao salvar perfil:', err);
      const rawErr = err as { message?: string; error_description?: string; details?: string };
      const errorMessage = rawErr?.message || rawErr?.error_description || rawErr?.details || (typeof err === 'string' ? err : 'Ocorreu uma falha ao comunicar com o servidor.');
      alert(`Não foi possível salvar suas alterações:\n\n${errorMessage}\n\nPor favor, tente novamente ou verifique as informações preenchidas.`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmNicheChange = async () => {
    if (!pendingNiche) return;
    
    const currentCount = formData.nicheChangeCount ?? user.nicheChangeCount ?? 0;
    const newCount = currentCount + 1;

    // 1. Reset instantâneo dos dados antigos (orçamentos, materiais, receitas, serviços)
    if (onResetNicheData) {
      try {
        await onResetNicheData();
      } catch (resetErr) {
        console.warn('Erro ao resetar dados locais do nicho:', resetErr);
      }
    }

    // 2. Update niche, count and last change date
    const updatedUser: UserProfile = { 
      ...formData, 
      profession: pendingNiche.professionName || formData.profession,
      niche: pendingNiche.name, 
      nicheCategory: pendingNiche.category || pendingNiche.name,
      nicheChangeCount: newCount,
      lastNicheChange: new Date().toISOString()
    };
    
    // 3. Clear state and trigger a global save to ensure persistence
    setFormData(updatedUser);
    setIsResetModalOpen(false);
    setPendingNiche(null);
    setIsSyncing(true);
    try {
      await onSave(updatedUser, financialData);
    } catch (err: unknown) {
      console.error('Erro ao salvar alteração de nicho:', err);
      const rawErr = err as { message?: string };
      const errorMessage = rawErr?.message || (typeof err === 'string' ? err : 'Falha na sincronização.');
      alert(`Não foi possível alterar o nicho no servidor:\n\n${errorMessage}`);
    } finally {
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
      syncTimerRef.current = setTimeout(() => setIsSyncing(false), 800);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user.id) return;
    
    const fileName = `logo_${user.id}.jpg`;
    setIsUploading(true);
    if (import.meta.env.DEV) {
      console.log('Iniciando upload e compactação do logo...', fileName);
    }

    try {
      // Compactação automática independente do tamanho original do arquivo (1MB, 10MB, 50MB+)
      const compressedBase64 = await compressImage(file, { purpose: 'logo' });
      
      // Update local state immediately for better UX
      setFormData(prev => ({ ...prev, logoBase64: compressedBase64 }));

      // Converte o base64 compactado para Blob leve
      const response = await fetch(compressedBase64);
      const blob = await response.blob();

      const { error } = await supabase.storage
        .from('logos')
        .upload(fileName, blob, { upsert: true, contentType: 'image/jpeg' });


      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('logos')
        .getPublicUrl(fileName);

      const finalUrl = `${publicUrl}?t=${new Date().getTime()}`;

      // Extrai a paleta de cores dominante da imagem do logo em background
      const palette = await extractDominantColor(compressedBase64);

      setFormData(prev => ({ ...prev, logoUrl: finalUrl }));
      setFinancialData(prev => ({ ...prev, logoUrl: finalUrl }));

      // PERSISTÊNCIA IMEDIATA NO BANCO DE DADOS (CADASTRO E VITRINE EM PARALELO)
      if (import.meta.env.DEV) {
        console.log('Persistindo logo_url e accent_color no banco de dados...', palette.primary);
      }
      
      // Salva em user_business_profiles
      await supabase
        .from('user_business_profiles')
        .upsert({
          user_id: user.id,
          logo_url: finalUrl
        }, {
          onConflict: 'user_id'
        });

      // Garante que storefront_profiles receba a logo e accent_color
      const { data: existingSf } = await supabase
        .from('storefront_profiles')
        .select('id, slug')
        .eq('user_id', user.id)
        .maybeSingle();

      if (existingSf) {
        await supabase
          .from('storefront_profiles')
          .update({
            logo_url: finalUrl,
            accent_color: palette.primary
          })
          .eq('id', existingSf.id);
      } else {
        const rawSlug = (user.businessName || user.ownerName || 'empresa')
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
        const autoSlug = (rawSlug && rawSlug !== 'meu-negocio' && rawSlug !== 'minha-empresa' && rawSlug !== 'empresa')
          ? `${rawSlug}-${user.id.slice(0, 6)}`
          : `empresa-${user.id.slice(0, 6)}`;
        try {
          await supabase
            .from('storefront_profiles')
            .insert({
              user_id: user.id,
              slug: autoSlug,
              display_name: user.businessName || user.ownerName || 'Meu Estabelecimento',
              logo_url: finalUrl,
              accent_color: palette.primary,
              is_published: true
            });
        } catch (err) {
          console.warn('[storefront_profiles] Sync silencioso de perfil no ProfileEditor:', err);
        }
      }

      await triggerAutoSave(
        { ...formData, logoUrl: finalUrl },
        { ...financialData, logoUrl: finalUrl }
      );

    } catch (err: unknown) {
      console.error('Erro no upload do logo:', err);
      alert('Não foi possível salvar o logotipo. Verifique se o arquivo é válido ou tente novamente mais tarde.');
    } finally {
      setIsUploading(false);
    }
  };

  React.useEffect(() => {
    let focusTimer: NodeJS.Timeout;
    const controller = new AbortController();
    
    const fetchCep = async () => {
      if (!zipCodeChangedByUser.current) return;
      const cleanCep = formData.zipCode?.replace(/\D/g, '');
      if (cleanCep?.length === 8) {
        try {
          const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`, {
            signal: controller.signal
          });
          const data = await response.json();
          
          if (!data.erro) {
            const formattedAddress = data.logradouro && data.bairro ? `${data.logradouro}, ${data.bairro}` : data.logradouro || data.bairro || formData.businessAddress;
            
            setFormData(prev => ({
              ...prev,
              businessAddress: formattedAddress,
              city: data.localidade || prev.city,
              state: data.uf || prev.state
            }));

            // Sync validation
            validateField('businessAddress', formattedAddress);
            validateField('city', data.localidade);
            validateField('state', data.uf);

            focusTimer = setTimeout(() => {
              numberInputRef.current?.focus();
            }, 100);
          } else {
            // CEP invalid or not found, clear fields for manual input
            setFormData(prev => ({ ...prev, businessAddress: '', city: '', state: '' }));
          }
        } catch (err: unknown) {
          if (err instanceof Error && err.name !== 'AbortError') {
            console.error("Erro na busca de CEP:", err);
            setFormData(prev => ({ ...prev, businessAddress: '', city: '', state: '' }));
          }
        } finally {
          zipCodeChangedByUser.current = false;
        }
      }
    };

    fetchCep();
    return () => {
      controller.abort();
      if (focusTimer) clearTimeout(focusTimer);
    };
  }, [formData.zipCode]);

  const filteredApiProfessions = React.useMemo(() => {
    return searchProfessions(formData.profession || '', 10);
  }, [searchProfessions, formData.profession]);

  const steps: { id: Step; label: string; icon: any }[] = [
    { id: 'business', label: 'Empresa', icon: Building2 },
    { id: 'personal', label: 'Pessoal', icon: UserIcon },
    { id: 'location', label: 'Local', icon: MapPin },
    { id: 'financial', label: 'Financeiro', icon: DollarSign }, // Added financial step
    { id: 'lgpd', label: 'Termos & Privacidade', icon: ShieldCheck },
  ];

  const priceHealth = calculatePriceHealth(formData, materials);

  const formatDateLong = (dateStr: string) => {
    if (!dateStr) return '';
    try {
      // Adicionamos T12:00:00 para evitar que o fuso horário (GMT-3) retroceda o dia
      const date = new Date(`${dateStr}T12:00:00`);
      return new Intl.DateTimeFormat('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
    } catch (e) {
      return dateStr;
    }
  };

  const handleFieldChange = <K extends keyof UserProfile>(field: K, value: UserProfile[K]) => {
    if (field === 'zipCode') {
      zipCodeChangedByUser.current = true;
    }
    const updated = { ...formData, [field]: value };
    setFormData(updated);
    validateField(field, value);
    
    const debounceFields = [
      'ownerName', 
      'businessName', 
      'zipCode', 
      'businessAddress', 
      'businessNumber', 
      'city', 
      'state', 
      'pixKey', 
      'profession', 
      'taxPercentage'
    ];
    if (debounceFields.includes(field)) {
      debouncedFormData.current = updated;
    } else {
      triggerAutoSave(updated, financialData);
    }
  };

  const handleFinancialChange = <K extends keyof FinancialConfig>(field: K, value: FinancialConfig[K]) => {
    const updated = { ...financialData, [field]: value };
    if (field === 'proLaboreGoal' || field === 'monthlyFixedCost' || field === 'weeklyWorkHours') {
      const income = updated.proLaboreGoal || 0;
      const costs = updated.monthlyFixedCost || 0;
      const hours = updated.weeklyWorkHours || 44;
      updated.baseHourValue = hours > 0 ? (income + costs) / (hours * 4.33) : 0;
    }
    setFinancialData(updated);
  };

  // ---------------------------------------------------------------------------
  // DERIVED VHB — Recalcula o Valor/Hora sempre que pró-labore, custos fixos
  // ou horas semanais mudarem. Isso corrige o bug onde o ProfileEditor salvava
  // os novos valores financeiros sem recalcular baseHourValue.
  // ---------------------------------------------------------------------------
  const derivedHourlyRate = React.useMemo(() => {
    const income = financialData.proLaboreGoal || 0;
    const costs = financialData.monthlyFixedCost || 0;
    const hours = financialData.weeklyWorkHours || 44;
    if (hours <= 0) return 0;
    return (income + costs) / (hours * 4.33);
  }, [financialData.proLaboreGoal, financialData.monthlyFixedCost, financialData.weeklyWorkHours]);

  const isEditing = !!user.ownerName && !!user.niche;

  return (
    <div className="fixed inset-0 w-full h-dvh bg-zinc-50 z-60 flex flex-col overflow-hidden font-sans box-border">
      {/* 1. FIXED HEADER + STEPPER (Shrink-0) */}
      <header className="bg-white border-b border-zinc-100 px-4 py-3 shrink-0 z-20 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-xl transition-colors">
              <ArrowLeft className="h-5 w-5 text-zinc-900" />
            </button>
            <div>
              <h1 className="text-base font-black text-zinc-900 leading-tight">Configurações</h1>
              <div className="flex items-center gap-1.5 mt-0.5">
                {isSyncing ? (
                  <>
                    <Loader2 className="h-2.5 w-2.5 text-blue-500 animate-spin" />
                    <span className="text-[8px] text-blue-500 font-black uppercase tracking-tighter">Sincronizando...</span>
                  </>
                ) : (
                  <>
                    <Cloud className={`h-2.5 w-2.5 ${lastSaved ? 'text-emerald-500' : 'text-zinc-300'}`} />
                    <span className="text-[8px] text-zinc-400 font-bold uppercase tracking-tighter">
                      {lastSaved ? `Salvo às ${lastSaved}` : 'Dados protegidos'}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
             {TUTORIAL_LINKS.cadastro ? (
               <a 
                 href={TUTORIAL_LINKS.cadastro}
                 target="_blank"
                 rel="noopener noreferrer"
                 title="Assistir Vídeo Tutorial desta tela"
                 className="p-1.5 bg-blue-50 border border-blue-200 text-blue-600 rounded-xl flex items-center gap-1 hover:bg-blue-100 transition-colors shadow-sm cursor-pointer"
               >
                 <PlayCircle className="h-4 w-4" />
               </a>
             ) : (
               <button 
                 title="Em breve: Vídeo Tutorial desta tela"
                 className="p-1.5 bg-zinc-100/50 border border-zinc-200/50 rounded-xl flex items-center gap-1 text-zinc-400 opacity-60 cursor-not-allowed"
               >
                 <PlayCircle className="h-4 w-4" />
               </button>
             )}
          </div>
        </div>

        {/* Progress Stepper - Integrated into header */}
        <div className="flex justify-between relative px-2">
          <div className="absolute top-4 left-4 right-4 h-0.5 bg-zinc-50 -z-10" />
          {steps.map((s, idx) => {
            const Icon = s.icon;
            const isActive = step === s.id;
            const isPast = steps.findIndex(x => x.id === step) > idx;
            
            return (
              <div 
                key={s.id} 
                className="flex flex-col items-center gap-1 cursor-pointer group"
                onClick={() => setStep(s.id)}
              >
                <div 
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                    isActive ? 'bg-zinc-900 text-white shadow-lg active:scale-95' : 
                    isPast ? 'bg-emerald-500 text-white' : 'bg-white border-2 border-zinc-100 text-zinc-400'
                  }`}
                >
                  {isPast ? <CheckCircle2 className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                </div>
                <span className={`text-[8px] font-black uppercase tracking-tighter ${isActive ? 'text-zinc-900' : 'text-zinc-400'}`}>
                  {s.label}
                </span>
              </div>
            );
          })}
        </div>
      </header>

      {/* 2. SCROLLABLE FORM BODY (Flex-1) */}
      <div className="flex-1 overflow-y-auto px-4 py-6 w-full overflow-x-hidden">
        <div className="max-w-xl mx-auto w-full box-border">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="space-y-6"
            >
              {step === 'personal' && (
                <PersonalStep
                  formData={formData}
                  errors={errors}
                  onFieldChange={handleFieldChange}
                  onLogout={onLogout}
                  formatDateLong={formatDateLong}
                />
              )}
              {step === 'business' && (
                <BusinessStep
                  formData={formData}
                  errors={errors}
                  isUploading={isUploading}
                  isNicheOpen={isNicheOpen}
                  setIsNicheOpen={setIsNicheOpen}
                  isSearchingProfessions={isSearchingProfessions}
                  filteredApiProfessions={filteredApiProfessions}
                  onFieldChange={handleFieldChange}
                  onLogoUpload={handleLogoUpload}
                  onProfessionSelect={(profession) => {
                    const currentNiche = formData.niche;
                    const isChangingNiche = Boolean(currentNiche && currentNiche.trim() !== '' && currentNiche !== profession.niche);

                    if (isChangingNiche) {
                      setPendingNiche({ name: profession.niche, category: profession.niche, professionName: profession.name });
                      setIsResetModalOpen(true);
                      setIsNicheOpen(false);
                      return;
                    }

                    const updatedUser = { 
                      ...formData, 
                      profession: profession.name, 
                      niche: profession.niche, 
                      nicheCategory: profession.niche,
                      lastNicheChange: new Date().toISOString()
                    };
                    setFormData(updatedUser);
                    setIsNicheOpen(false);
                    setIsSyncing(true);
                    onSave(updatedUser, financialData);
                    if (professionSyncTimerRef.current) clearTimeout(professionSyncTimerRef.current);
                    professionSyncTimerRef.current = setTimeout(() => setIsSyncing(false), 800);
                  }}
                />
              )}
              {step === 'location' && (
                <LocationStep
                  formData={formData}
                  errors={errors}
                  onFieldChange={handleFieldChange}
                  numberInputRef={numberInputRef}
                />
              )}
              {step === 'financial' && (
                <FinancialStep
                  formData={formData}
                  financialData={financialData}
                  derivedHourlyRate={derivedHourlyRate}
                  onFinancialChange={handleFinancialChange}
                  onTriggerAutoSave={() => triggerAutoSave(formData, financialData)}
                  onOpenPaywall={(plan) => {
                    setPaywallInitialPlan(plan || (formData.plan === 'Pro' ? 'specialist' : 'annual'));
                    setIsPaywallOpen(true);
                  }}
                  onOpenCancelSurvey={() => {
                    setCancelStep('survey');
                    setSelectedReason('');
                    setCancelError(null);
                    setCancelSuccess(false);
                    setIsCancelSurveyOpen(true);
                  }}
                  formatDateLong={formatDateLong}
                />
              )}
              {step === 'lgpd' && (
                <LgpdStep
                  formData={formData}
                  onConsentChange={(consentData) => handleFieldChange('lgpdConsent', consentData)}
                  onOpenTerms={() => setIsTermsModalOpen(true)}
                  onOpenPrivacy={() => setIsPrivacyModalOpen(true)}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* 3. FIXED FOOTER (Shrink-0) */}
      <footer className="bg-white border-t border-zinc-100 p-4 shrink-0 shadow-[0_-4px_10px_rgba(0,0,0,0.02)] w-full box-border">
        <div className="max-w-xl mx-auto flex gap-3 w-full box-border">
          {step !== 'business' && (
            <button
              onClick={() => {
                const idx = steps.findIndex(s => s.id === step);
                setStep(steps[idx - 1].id);
              }}
              className="px-6 py-4 rounded-2xl border border-zinc-200 text-zinc-600 font-bold text-sm hover:bg-zinc-50 transition-all active:scale-95"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
          )}

          {isEditing && (
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex-1 py-4 px-2 rounded-2xl border border-zinc-300 text-zinc-500 font-bold text-sm hover:bg-zinc-50 transition-all flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50"
            >
               {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4" />
                  Salvar e sair
                </>
              )}
            </button>
          )}
          
          {step === 'lgpd' ? (
            <button
              onClick={handleSave}
              disabled={isSaving}
              className={`flex-2 py-4 rounded-2xl font-black text-sm uppercase tracking-widest shadow-lg transition-all flex items-center justify-center gap-2 active:scale-95 ${
                !isSaving ? 'bg-blue-600 text-white shadow-blue-600/20 hover:bg-blue-700' : 'bg-zinc-200 text-zinc-400'
              }`}
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  Finalizar
                  <CheckCircle2 className="h-4 w-4" />
                </>
              )}
            </button>
          ) : (
            <button
              onClick={() => {
                const idx = steps.findIndex(s => s.id === step);
                setStep(steps[idx + 1].id);
              }}
              className="flex-2 py-4 rounded-2xl font-black text-sm uppercase tracking-widest bg-zinc-900 text-white shadow-lg shadow-zinc-900/20 hover:bg-black transition-all flex items-center justify-center gap-2 active:scale-95"
            >
              Próximo
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </footer>

      <NicheResetModal 
        isOpen={isResetModalOpen}
        currentCount={formData.nicheChangeCount ?? user.nicheChangeCount ?? 0}
        onClose={() => {
          setIsResetModalOpen(false);
          setPendingNiche(null);
        }}
        onConfirm={handleConfirmNicheChange}
        newNiche={pendingNiche?.name || ''}
      />

      {/* MODAL: Termos de Uso */}
      <AnimatePresence>
        {isTermsModalOpen && (
          <div className="fixed inset-0 z-100 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsTermsModalOpen(false)}
              className="absolute inset-0 bg-zinc-950/60 backdrop-blur-xs"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden border border-zinc-100 flex flex-col max-h-[85vh] z-10"
            >
              <div className="p-6 border-b border-zinc-100 flex justify-between items-center bg-zinc-50">
                <h3 className="text-xs font-black text-zinc-900 uppercase tracking-wider">Termos e Condições de Uso</h3>
                <button 
                  onClick={() => setIsTermsModalOpen(false)}
                  className="p-1.5 hover:bg-zinc-100 rounded-full transition-colors text-zinc-400"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto flex-1">
                <TermsContent />
              </div>
              <div className="p-4 border-t border-zinc-100 bg-zinc-50 flex justify-end">
                <button
                  type="button"
                  onClick={() => setIsTermsModalOpen(false)}
                  className="px-6 py-2.5 bg-zinc-900 text-white font-bold rounded-xl text-xs uppercase tracking-wider hover:opacity-90 transition-all cursor-pointer"
                >
                  Entendido
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MODAL: Política de Privacidade */}
      <AnimatePresence>
        {isPrivacyModalOpen && (
          <div className="fixed inset-0 z-100 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsPrivacyModalOpen(false)}
              className="absolute inset-0 bg-zinc-950/60 backdrop-blur-xs"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden border border-zinc-100 flex flex-col max-h-[85vh] z-10"
            >
              <div className="p-6 border-b border-zinc-100 flex justify-between items-center bg-zinc-50">
                <h3 className="text-xs font-black text-zinc-900 uppercase tracking-wider">Política de Privacidade</h3>
                <button 
                  onClick={() => setIsPrivacyModalOpen(false)}
                  className="p-1.5 hover:bg-zinc-100 rounded-full transition-colors text-zinc-400"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto flex-1">
                <PrivacyContent />
              </div>
              <div className="p-4 border-t border-zinc-100 bg-zinc-50 flex justify-end">
                <button
                  type="button"
                  onClick={() => setIsPrivacyModalOpen(false)}
                  className="px-6 py-2.5 bg-zinc-900 text-white font-bold rounded-xl text-xs uppercase tracking-wider hover:opacity-90 transition-all cursor-pointer"
                >
                  Entendido
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MODAL: Upgrade Paywall */}
      <PaywallModal 
        isOpen={isPaywallOpen}
        initialPlan={paywallInitialPlan}
        onClose={() => setIsPaywallOpen(false)}
      />

      {/* MODAL: Churn Survey (Fluxo de Cancelamento) */}
      <CancelSurveyModal
        isOpen={isCancelSurveyOpen}
        onClose={() => { if (!isCanceling && !cancelSuccess) setIsCancelSurveyOpen(false); }}
        cancelStep={cancelStep}
        setCancelStep={setCancelStep}
        selectedReason={selectedReason}
        setSelectedReason={setSelectedReason}
        isCanceling={isCanceling}
        cancelError={cancelError}
        cancelSuccess={cancelSuccess}
        onConfirmCancel={handleCancelSubscription}
      />
    </div>
  );
}
